/**
 * /admin/connect-diagnose — paste a seller email, get a plain-English
 * dump of their Stripe Connect state in 30 seconds.
 *
 * Surfaces everything our user-facing banner hides — `disabled_reason`,
 * `pending_verification`, identity rejection codes from `requirements.errors`,
 * the full `currently_due / past_due / eventually_due` arrays, AND a
 * one-line recommended next action (e.g. "they tapped Save and finish
 * later — send them the Express Dashboard login link to complete").
 *
 * Use when a seller has tried Connect onboarding 3+ times and the
 * banner still says "Still needed". The diagnostic distinguishes
 * between:
 *   • Stripe rejected the account (disabled_reason set)
 *   • Stripe rejected a specific field (errors[] populated)
 *   • Stripe is reviewing (pending_verification[])
 *   • Seller didn't submit (currently_due[] only)
 *   • Healthy account with stale cache (everything green)
 */
import React, { useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BankIcon as Bank, StethoscopeIcon as Stethoscope, MagnifyingGlassIcon as MagnifyingGlass, CheckCircleIcon as CheckCircle, WarningIcon as Warning, XCircleIcon as XCircle, InfoIcon as Info } from "@phosphor-icons/react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";

export default function AdminConnectDiagnose() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await api.get("/connect/admin/diagnose", { params: { email: email.trim() } });
      setResult(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-12" data-testid="admin-connect-diagnose-page">
        <div className="flex items-center gap-3 mb-2">
          <Stethoscope size={26} weight="duotone" className="text-crimson" />
          <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight">Stripe Connect diagnose</h1>
        </div>
        <p className="text-[13.5px] text-[#6B6B6B] leading-snug mb-8 max-w-2xl">
          Paste a seller&apos;s email to see EXACTLY what Stripe says is blocking their payouts —
          including the fields the user-facing banner can&apos;t show
          (<code className="text-[12px] bg-[#FBF7EC] px-1 py-0.5 rounded">disabled_reason</code>,{" "}
          <code className="text-[12px] bg-[#FBF7EC] px-1 py-0.5 rounded">requirements.errors</code>,{" "}
          <code className="text-[12px] bg-[#FBF7EC] px-1 py-0.5 rounded">pending_verification</code>).
          Read the <strong>Recommendation</strong> line first — it tells you exactly what to tell the seller.
        </p>

        <form onSubmit={run} className="flex gap-2 mb-8">
          <Input
            type="email"
            placeholder="seller@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="diagnose-email-input"
            className="h-11"
            disabled={busy}
          />
          <Button
            type="submit"
            disabled={busy || !email.trim()}
            data-testid="diagnose-run-btn"
            className="h-11 px-5 bg-ink hover:bg-crimson text-white"
          >
            <MagnifyingGlass size={16} weight="bold" className="mr-1.5" />
            {busy ? "Looking up…" : "Diagnose"}
          </Button>
        </form>

        {result && <DiagnoseResultCard result={result} />}
      </div>
    </AppShell>
  );
}

function DiagnoseResultCard({ result }) {
  if (!result.found) {
    return (
      <div className="rounded-xl border border-[#EAEAEA] bg-white p-6" data-testid="diagnose-not-found">
        <div className="flex items-center gap-2 mb-1">
          <XCircle size={18} weight="fill" className="text-stone-500" />
          <h2 className="font-spectral text-xl">No user with that email.</h2>
        </div>
        <p className="text-[13px] text-[#6B6B6B]">
          Check the spelling, or the user may have signed up with a different email.
        </p>
      </div>
    );
  }
  if (!result.has_connect_account) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-6" data-testid="diagnose-no-connect">
        <div className="flex items-center gap-2 mb-1">
          <Info size={18} weight="fill" className="text-amber-700" />
          <h2 className="font-spectral text-xl">No Connect account yet.</h2>
        </div>
        <p className="text-[13px] text-[#6B6B6B]">{result.recommendation}</p>
      </div>
    );
  }
  if (result.stripe_retrieve_failed) {
    return (
      <div className="rounded-xl border border-red-300 bg-red-50 p-6" data-testid="diagnose-stripe-error">
        <h2 className="font-spectral text-xl text-red-900 mb-1">Stripe API call failed</h2>
        <p className="text-[12px] text-red-800 font-mono">{result.error}</p>
      </div>
    );
  }

  const healthy = result.payouts_enabled && !result.disabled_reason && (result.currently_due?.length || 0) === 0;
  const HeaderIcon = healthy ? CheckCircle : Warning;

  return (
    <div className="space-y-4" data-testid="diagnose-result">
      <div className={`rounded-xl border-2 p-5 ${healthy ? "border-emerald-300 bg-emerald-50" : "border-crimson/40 bg-[#FFF5F5]"}`}>
        <div className="flex items-start gap-3">
          <HeaderIcon size={24} weight="fill" className={healthy ? "text-emerald-700" : "text-crimson"} />
          <div className="flex-1">
            <h2 className="font-spectral text-2xl tracking-tight mb-1">
              {result.user.name || result.user.email}
            </h2>
            <p className="text-[12px] text-[#6B6B6B] font-mono">
              acct: <strong>{result.stripe_account_id}</strong> · {result.country} · {result.default_currency?.toUpperCase()}
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Flag label="charges_enabled" on={result.charges_enabled} />
              <Flag label="payouts_enabled" on={result.payouts_enabled} />
              <Flag label="details_submitted" on={result.details_submitted} />
              {result.disabled_reason && (
                <span className="px-2 py-0.5 rounded-full bg-red-200 text-red-900 text-[10px] font-bold uppercase tracking-[0.12em]">
                  disabled: {result.disabled_reason}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-[#E8D9A6] bg-[#FBF7EC] p-5">
        <div className="flex items-center gap-2 mb-2">
          <Bank size={18} weight="duotone" className="text-[#8A6A00]" />
          <h3 className="font-spectral text-lg">Recommendation</h3>
        </div>
        <p className="text-[13.5px] text-ink leading-relaxed" data-testid="diagnose-recommendation">
          {result.recommendation}
        </p>
      </div>

      {(result.currently_due?.length || 0) > 0 && (
        <Section title={`Still needed (currently_due) — ${result.currently_due.length}`}>
          <RequirementList raw={result.currently_due} labels={result.currently_due_labels} />
        </Section>
      )}
      {(result.past_due?.length || 0) > 0 && (
        <Section title={`Past due — ${result.past_due.length}`} variant="alert">
          <RequirementList raw={result.past_due} labels={result.past_due_labels} />
        </Section>
      )}
      {(result.pending_verification?.length || 0) > 0 && (
        <Section title={`Stripe is reviewing — ${result.pending_verification.length}`} variant="info">
          <RequirementList raw={result.pending_verification} labels={result.pending_verification} />
        </Section>
      )}
      {(result.errors?.length || 0) > 0 && (
        <Section title={`Errors (${result.errors.length})`} variant="alert">
          <ul className="text-[12.5px] text-ink space-y-2">
            {result.errors.map((e, i) => (
              <li key={i} className="bg-white border border-red-200 rounded-md px-3 py-2">
                <div className="font-mono text-[11px] text-red-900">
                  <strong>{e.requirement}</strong> · {e.code}
                </div>
                <div className="text-[12.5px] text-[#3F3F3F] mt-0.5">{e.reason}</div>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {(result.eventually_due?.length || 0) > 0 && (
        <Section title={`Eventually due — ${result.eventually_due.length}`} variant="muted">
          <RequirementList raw={result.eventually_due} labels={result.eventually_due} />
        </Section>
      )}
    </div>
  );
}

function Flag({ label, on }) {
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] border ${
        on ? "bg-emerald-100 text-emerald-900 border-emerald-300" : "bg-stone-100 text-stone-700 border-stone-300"
      }`}
    >
      {label}: {on ? "✓" : "✗"}
    </span>
  );
}

function Section({ title, variant = "default", children }) {
  const tone = {
    alert: "border-red-200 bg-red-50",
    info: "border-sky-200 bg-sky-50",
    muted: "border-stone-200 bg-stone-50",
    default: "border-[#EAEAEA] bg-white",
  }[variant];
  return (
    <div className={`rounded-xl border ${tone} p-4`}>
      <h3 className="font-spectral text-[16px] mb-2">{title}</h3>
      {children}
    </div>
  );
}

function RequirementList({ raw, labels }) {
  return (
    <ul className="text-[12.5px] text-ink space-y-1 list-disc pl-5">
      {(labels || raw || []).map((label, i) => (
        <li key={i}>
          {label}
          {raw && raw[i] && raw[i] !== label && (
            <span className="text-[11px] text-[#9A9A9A] ml-2 font-mono">({raw[i]})</span>
          )}
        </li>
      ))}
    </ul>
  );
}
